import React, { useState, useCallback } from 'react';
import { isNil, removeEventEffect } from '@common/utils';
import { PRODUCT_TYPE_CODE, INVEST_TYPE_CODE1, INVEST_TYPE_CODE2 } from '@common/constants';

/* @TODO: 원래는 개발에서만 노출하여야 하나, 통합테스트시기에는 운영에도 노출 [추후 수정] */
/* 점수 계산 (개발계에서만 테스트용) */
const SuitabilityTestScore = ({
  type,
  customerInfo,
  answers,
  setNotSuitableReasons,
}) => {
  const [score, setScore] = useState(''); // 개발계 표시용
  const [invTypeText, setInvTypeText] = useState(''); // 개발계 표시용
  const [prdTypeText, setPrdTypeText] = useState(''); // 개발계 표시용

  /**
   * 미성년자 여부 조회
   */
  const isMinor = useCallback(
    customerInfo =>
      customerInfo.custKind !== 'C' &&
      !isNil(customerInfo.legalAge) &&
      19 > Number(customerInfo.legalAge),
    [],
  );

  const calculateScoreVariable = e => {
    const isAllCheck = [
      3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17,
    ].every(n => {
      if (n === 11) {
        return answers[n]?.length >= 1;
      } else {
        return answers[n] !== undefined;
      }
    });

    // 투자성향 코드 조회
    const getInvTypeCode = score => {
      const getCodeValueBf = score => {
        switch (true) {
          case 19 >= score:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험회피형').value;
          case 19 < score && score <= 30:
            return INVEST_TYPE_CODE1.find(c => c.label === '안정추구형').value;
          case 30 < score && score <= 45:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험중립형').value;
          case 45 < score && score <= 60:
            return INVEST_TYPE_CODE1.find(c => c.label === '적극투자형').value;
          case 60 < score && score <= 73:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험선호형').value;
        }
      };
      const getCodeValueAf = score => {
        switch (true) {
          case 19 >= score:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험회피형').value;
          case 19 < score && score <= 30:
            return INVEST_TYPE_CODE2.find(c => c.label === '안정추구형').value;
          case 30 < score && score <= 45:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험중립형').value;
          case 45 < score && score <= 60:
            return INVEST_TYPE_CODE2.find(c => c.label === '적극투자형').value;
          case 60 < score && score <= 73:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험선호형').value;
        }
      };
      const result = formData.testDate < '20240401' ? getCodeValueBf(score) : getCodeValueAf(score);

      return result;
    };

    // 상품성향 코드 조회
    const getPrdTypeCode = (isSuitable, answers) => {
      // 변액보험 부적합인 경우, 부적합코드 반환
      if (!isSuitable) {
        return PRODUCT_TYPE_CODE.find(c => c.label === '부적합').value;
      } else {
        // 변액보험 적합인 경우, 2번 항목 답변 확인
        switch (answers[2]) {
          case 0: // 위험에 대비한 가족의 보장자산 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '보장형').value;
          case 1: // 장기 저축을 통한 목돈 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '저축형').value;
          case 2: // 노후를 위한 연금자산 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '연금형').value;
          case 3: // 단기 재산증식
            return PRODUCT_TYPE_CODE.find(c => c.label === '부적합').value;
        }
      }
    };

    // 투자성향 조회용 점수 조회
    const getScore = answers => {
      // maximum 73
      const scoreList = [
        { no: 3, scores: [1, 2, 3, 4] },
        { no: 4, scores: [1, 2, 3, 4, 5] },
        { no: 5, scores: [1, 2, 3, 4] },
        { no: 6, scores: [1, 2, 3, 4, 5] },
        { no: 7, scores: [5, 4, 3, 2, 0] },
        { no: 8, scores: [5, 4, 3, 2, 0] },
        { no: 9, scores: [5, 4, 3, 2, 1] },
        { no: 10, scores: [1, 2, 3, 4, 5] },
        { no: 11, scores: [1, 2, 3, 4, 5, 6] },
        { no: 12, scores: [0, 2, 3, 4] },
        { no: 13, scores: [1, 2, 3, 4, 5] },
        { no: 14, scores: [0, 1, 2, 3, 4, 5, 6] },
        { no: 15, scores: [1, 2, 3, 4, 5] },
        { no: 16, scores: [1, 2, 3, 4] },
        { no: 17, scores: [1, 3, 5] },
      ];

      const result = scoreList.reduce((acc, v) => {
        if (v.no === 11) {
          if (!answers[v.no] || answers[v.no]?.length === 0) return acc; // 11번 문항 선택 없을 시
          // 복수 선택 중 최대값
          const maxIndex = Math.max(...answers[v.no]);
          const currentScore = !isNaN(v.scores[maxIndex])
            ? v.scores[maxIndex]
            : 0;

          return acc + currentScore;
        }

        const currentScore = !isNaN(v.scores[answers[v.no]])
          ? v.scores[answers[v.no]]
          : 0;

        return acc + currentScore;
      }, 0);

      return result;
    };

    // 변액보험 적합 여부 조회 (true: 적합, false: 부적합)
    const getSuitable = (customerInfo, answers) => {
      // 나이가 취약금융소비자 나이가 아닌데 취약 금융소비자 해댱 여부 예 체크시 부적합
      if (
        answers[0] !== 1 &&
        customerInfo.custKind !== 'C' &&
        65 > customerInfo.legalAge &&
        !isMinor(customerInfo)
      ) {
        return false;
      }

      // 취약금융소비자(0번항목) yes (예: 0, 아니오 : 1, 친권자/후견인: 3)
      if ([0, 3].includes(answers[0])) {
        if (
          // 만 65세 이상 고령투자자는 10, 11번 문항에 투자경험 없음(0번)으로 답변 시
          (customerInfo.custKind !== 'C' &&
            65 <= customerInfo.legalAge &&
            answers[10] === 0 &&
            (answers[11] ?? []).some(a => 0 === a)) ||
          // 미성년자는 친권자/후견인 미체크시 (0번항목 3번)
          isMinor(customerInfo)
        ) {
          return false;
        }
      }

      if (
        [1, 2, 3].includes(answers[1]) || // 1번 항목 일반보험(비변액보험)(1번) || 주식,채권 등 금융투자상품(2번) || 예금,적금 선택시 (3)
        3 === answers[2] || // 2번 항목 단기 재산증식 목적 선택 시 (3번)
        4 === answers[7] || // 7번 3년 미만(4번) 선택 시(단, 일시납 등 납입기간 3년 미만 상품권유)
        4 === answers[8] || // 8번 7년 미만(4번) 선택 시
        0 === answers[12] || // 12번 원금은 반드시 보존되어야 한다(0번) 선택 시
        0 === answers[14] // 14번 어떠한 경우에도 손실이 나면 안된다(0번) 선택 시
      ) {
        return false;
      }

      return true;
    };

    removeEventEffect(e);

    const score = getScore(answers);
    const isSuitable = getSuitable(customerInfo, answers);
    const invTypeCode = getInvTypeCode(score);
    const invTypeText =
      formData.testDate < '20240401' ? (INVEST_TYPE_CODE1.find(c => invTypeCode === c.value)?.label ?? '') : (INVEST_TYPE_CODE2.find(c => invTypeCode === c.value)?.label ?? '');
    const prdTypeCode = getPrdTypeCode(isSuitable, answers);
    const prdTypeText =
      PRODUCT_TYPE_CODE.find(c => prdTypeCode === c.value)?.label ?? '';

    setScore(score);
    setInvTypeText(isAllCheck ? invTypeText : '미체크존재');
    setPrdTypeText(isAllCheck ? prdTypeText : '미체크존재');
  };

  // 외화 적합성 스코어 계산
  const calculateScoreCurrency = e => {
    const isAllCheck = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].every(n => {
      if (n === 10) {
        return answers[n]?.length >= 1;
      } else {
        return answers[n] !== undefined;
      }
    });

    // 투자성향 코드 조회
    const getInvTypeCode = score => {
      const getCodeValueBf = score => {
        switch (true) {
          case 15 >= score:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험회피형').value;
          case 15 < score && score <= 22:
            return INVEST_TYPE_CODE1.find(c => c.label === '안정추구형').value;
          case 22 < score && score <= 33:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험중립형').value;
          case 33 < score && score <= 42:
            return INVEST_TYPE_CODE1.find(c => c.label === '적극투자형').value;
          case 42 < score && score <= 54:
            return INVEST_TYPE_CODE1.find(c => c.label === '위험선호형').value;
        }
      };
      const getCodeValueAf = score => {
        switch (true) {
          case 15 >= score:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험회피형').value;
          case 15 < score && score <= 22:
            return INVEST_TYPE_CODE2.find(c => c.label === '안정추구형').value;
          case 22 < score && score <= 33:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험중립형').value;
          case 33 < score && score <= 42:
            return INVEST_TYPE_CODE2.find(c => c.label === '적극투자형').value;
          case 42 < score && score <= 54:
            return INVEST_TYPE_CODE2.find(c => c.label === '위험선호형').value;
        }
      };
      const result = formData.testDate < '20240401' ? getCodeValueBf(score) : getCodeValueAf(score);

      return result;
    };

    // 상품성향 코드 조회
    const getPrdTypeCode = (isSuitable, answers) => {
      // 외화보험 부적합인 경우, 부적합코드 반환
      if (!isSuitable) {
        return PRODUCT_TYPE_CODE.find(c => c.label === '부적합').value;
      } else {
        // 외화보험 적합인 경우, 2번 항목 답변 확인
        switch (answers[2]) {
          case 0: // 위험에 대비한 가족의 보장자산(외화) 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '보장형').value;
          case 1: // 노후를 위한 연금자산 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '연금형').value;
          case 2: // 장기 저축을 통한 목돈 마련
            return PRODUCT_TYPE_CODE.find(c => c.label === '저축형').value;
          case 3: // 환율 변동에 따른 재산 증식 등 단기적인 이익 취득 목적
            return PRODUCT_TYPE_CODE.find(c => c.label === '부적합').value;
        }
      }
    };

    // 투자성향 조회용 점수 조회
    const getScore = answers => {
      // maximum 73
      const scoreList = [
        { no: 4, scores: [1, 2, 3, 4] },
        { no: 5, scores: [1, 2, 3, 4, 5] },
        { no: 6, scores: [1, 2, 3, 4, 5] },
        { no: 7, scores: [5, 4, 3, 2, 0] },
        { no: 8, scores: [5, 4, 3, 2, 0, 0] },
        { no: 9, scores: [5, 4, 3, 2, 1] },
        { no: 10, scores: [1, 2, 3, 4, 5, 6, 2] },
        { no: 11, scores: [1, 2, 3, 4, 5] },
        { no: 12, scores: [0, 2, 3, 4] },
        { no: 13, scores: [0, 1, 2, 3, 4, 5] }, // to do 갯수가 하나 부족하네
        { no: 14, scores: [1, 3, 5] },
      ];

      const result = scoreList.reduce((acc, v) => {
        if (v.no === 10) {
          if (!answers[v.no] || answers[v.no]?.length === 0) return acc; // 10번 문항 선택 없을 시

          const multiAnswers = answers[v.no];
          const scores = multiAnswers.map(answer => parseInt(v.scores[answer]));
          const maxScore = Math.max(...scores);
          return acc + maxScore;

          // 복수 선택 중 최대값
          // const maxIndex = Math.max(...answers[v.no]);
          // const currentScore = !isNaN(v.scores[maxIndex])
          //   ? v.scores[maxIndex]
          //   : 0;
          // return acc + currentScore;
        } else {
          const currentScore = !isNaN(v.scores[answers[v.no]])
            ? v.scores[answers[v.no]]
            : 0;

          return acc + currentScore;
        }
      }, 0);

      return result;
    };

    // 외화보험 부적합 원인 조회 (true: 적합, false: 부적합)
    const getNotSuitableReasons = (customerInfo, answers) => {
      const notSuitableReasons = [];
      // 취약금융소비자(0번항목) yes (예: 0, 아니오 : 1, 친권자/후견인: 3)
      customerInfo.custKind !== 'C' &&
        65 <= customerInfo.legalAge &&
        answers[0] === 0 &&
        answers[10]?.includes(0) &&
        answers[14] === 0 &&
        notSuitableReasons.push({
          index: 'f',
          exceptRsns: [10, 14],
          isWeak: true,
        }); // 진단자 나이 만 65세 이상 AND [취약금융소비자 해당여부 기재란 Yes] AND [⑩가입경험없음] AND [⑭이해수준이 현저히 낮음]
      customerInfo.custKind !== 'C' &&
        !isMinor(customerInfo) &&
        65 > customerInfo.legalAge &&
        answers[0] === 0 &&
        notSuitableReasons.push({ index: 'k', exceptRsns: [], isWeak: true }); // 진단자 나이 만 19세 이상~ 만 65세 미만 AND [취약금융소비자 해당여부 기재란 Yes]
      0 !== answers[1] &&
        notSuitableReasons.push({ index: 'a', exceptRsns: [1] }); // 외화보험 (외화변액보험, 외화일반보험) 외 모든 항목
      3 === answers[2] &&
        notSuitableReasons.push({ index: 'b', exceptRsns: [2] }); // 환율변동에 따른 재산 증식 등 단기적인 이익 취득 목적
      0 === answers[2] &&
        answers[3]?.some(v => [2, 3].includes(v)) &&
        notSuitableReasons.push({ index: 'i', exceptRsns: [2, 3] }); // 위험에 대비한 보장자산(외화) 마련 AND
      ((1 === answers[2] && answers[3]?.some(v => [0, 1].includes(v))) || // 노후를 위한 연금자산 마련 AND
        (2 === answers[2] && answers[3]?.some(v => [0, 1].includes(v)))) && // 장기 저축을 통한 목돈 마련 AND
        notSuitableReasons.push({ index: 'j', exceptRsns: [2, 3] }); // 장기 저축을 통한 목돈 마련 AND
      answers[3]?.some(v => [4, 5].includes(v)) &&
        notSuitableReasons.push({ index: 'c', exceptRsns: [3] }); // [가끔 해외여행경비 활용] OR [안정적 재산증식 수단으로 손실가능성이 없는 외화를 활용]
      4 === answers[7] &&
        notSuitableReasons.push({ index: 'd', exceptRsns: [7] }); // [3년 미만]
      [4, 5].includes(answers[8]) &&
        notSuitableReasons.push({ index: 'e', exceptRsns: [8] }); // [5년 미만] OR [5년~7년 미만]

      0 === answers[2] &&
        [2, 3, 4].includes(answers[9]) &&
        notSuitableReasons.push({ index: 'g', exceptRsns: [2, 9] }); // ②위험에 대비한 보장자산(외화) 마련] AND [⑨가능성이 있음(50% 이상) or 높음(70% 이상) or 매우 높음(90% 이상)]

      if (
        0 === answers[2] &&
        answers[10]?.includes(0) &&
        !answers[3]?.some(v => [0, 1].includes(v)) &&
        answers[7] !== 0 &&
        ![0, 1].includes(answers[8])
      ) {
        notSuitableReasons.push({ index: 'h', exceptRsns: [2, 10, 11] }); // [②위험에 대비한 보장자산(외화) 마련] AND [⑩,⑪가입경험없음] ★단, 아래 조건을 모두 충족한경우, [부적합]이 아닌 상품성향 [보장형]
      }
      return notSuitableReasons;
    };

    const notSuitableReasons = getNotSuitableReasons(customerInfo, answers); // 부적합 이유
    setNotSuitableReasons(notSuitableReasons);

    removeEventEffect(e);

    const score = getScore(answers);
    const isSuitable = notSuitableReasons?.length === 0;
    const invTypeCode = getInvTypeCode(score);
    const invTypeText =
      formData.testDate < '20240401' ? (INVEST_TYPE_CODE1.find(c => invTypeCode === c.value)?.label ?? '') : (INVEST_TYPE_CODE2.find(c => invTypeCode === c.value)?.label ?? '');
    const prdTypeCode = getPrdTypeCode(isSuitable, answers);
    const prdTypeText =
      PRODUCT_TYPE_CODE.find(c => prdTypeCode === c.value)?.label ?? '';

    setScore(score);
    setInvTypeText(isAllCheck ? invTypeText : '미체크존재');
    setPrdTypeText(isAllCheck ? prdTypeText : '미체크존재');
  };

  // 테스트용 점수 계산 (개발계에서만 노출)
  const handleDisplayTestScore = e => {
    if (type === 'variable') calculateScoreVariable(e);
    else if (type === 'currency') calculateScoreCurrency(e);
  };

  return (
    <>
      &nbsp;&nbsp;&nbsp;
      <button className="btn-s blue2 side-r" onClick={handleDisplayTestScore}>
        점수 계산
      </button>
      <button className="btn-s side-r">점수 : {score}</button>
      <button className="btn-s side-r">소비자 성향 : {invTypeText}</button>
      <button className="btn-s side-r">진단 결과 : {prdTypeText}</button>
    </>
  );
};

SuitabilityTestScore.propTypes = {
  type: $propTypes.string,
  customerInfo: $propTypes.object,
  answers: $propTypes.object,
  setNotSuitableReasons: $propTypes.func,
};

SuitabilityTestScore.defaultProps = {
  type: 'variable',
};

export default SuitabilityTestScore;
